import type { JSX } from 'react';

import type { IndexMetadata } from '@/entities/market';
import type { IndexSymbol } from '@/entities/symbol';
import type { TemplateChartSeries } from '@/shared/types/charts';

export type { IndexMetadata };

// Moved from components/Share/ShareBlock/index.tsx
export enum ShareBlockNames {
  ForwardCurveChart = 'Forward Curve Chart',
  SeasonalChart = 'Seasonal Chart',
  SpotChart = 'Spot Chart',
  Feed = 'Feed',
  Table = 'Table',
  Tables = 'Tables',
  CSVTable = 'CSV Table',
  CustomChart = 'Custom Chart',
  Files = 'Data lake files'
}

// Moved from components/types.ts
export type CommoditiesTableRowMetadata = {
  Title: string;
  Currency: string;
  Units: string;
  Alias?: string;
};

export type CommoditiesTableRowType = {
  shortTitle: string;
  date: string;
  last: string;
  delta: string;
  metadata: CommoditiesTableRowMetadata;
  periodAbs: string;
  diff: string | null;
  diffDelta: string | null;
  diffMetadata: CommoditiesTableRowMetadata;
  diffPeriodAbs: string;
  symbol: IndexSymbol;
  diffSymbol: IndexSymbol;
};

export type CommoditiesTableSectionType = {
  name: string;
  rows: CommoditiesTableRowType[];
};

export type CommoditiesTableTabType = {
  name: string;
  sections: CommoditiesTableSectionType[];
};

export type ChartsBlockTabType<T> = {
  name: string;
  series: TemplateChartSeries[];
  metadata?: Record<string, string>;
  component: ShareBlockNames;
  blockIndex: number;
} & T;

export type BaseMiniTableRow = {
  cells: string[];
  columns?: string[];
  layout?: {
    highlight: 'true';
  };
  metadata?: Record<string, string>;
};

export type MiniTableBodyRow = BaseMiniTableRow & {
  date: string[];
  symbol: IndexSymbol;
  from: string;
  diffSymbol: IndexSymbol;
};

export type MiniTableRow = BaseMiniTableRow | MiniTableBodyRow;

export type MiniTableType = { rows: MiniTableRow[]; name: string };

export type MiniTableTableTab = {
  name: string;
  tables: MiniTableType[];
};

// Moved from components/MultiGroupTable/types.ts
export type SymbolPropertyConfig = {
  name: string;
  label: string;
};

export type MultiGroupTableRowType = {
  shortTitle: string;
  symbolA: IndexSymbol;
  symbolAProperties: SymbolPropertyConfig[];
  symbolAMetadata?: IndexMetadata | null;
  symbolB: IndexSymbol;
  symbolBProperties: SymbolPropertyConfig[];
  symbolBMetadata?: IndexMetadata | null;
  date?: string;
  last?: string;
  delta?: string | null;
  periodAbs?: string;
  bValues?: Record<string, string | undefined>;
};

export type MultiGroupTableSectionType = {
  name: string;
  rows: MultiGroupTableRowType[];
};

export type MultiGroupTableTabType = {
  name: string;
  sections: MultiGroupTableSectionType[];
};

// Moved from components/Tiles/types.ts
export type TileType = {
  code: string;
  shortTitle: string;
  value: string;
  delta: string;
  deltaPercentage: string;
  id: string;
};

export type BaseMarketsComponentType = {
  name: string;
  id: string;
  metadata?: Record<string, string>;
  layout?: { fullWidth?: boolean; height?: number | string } | Record<string, string>;
};

export type SpotHistoryTableColumnType = {
  code?: string | null;
  symbol?: IndexSymbol | null;
  alias?: string | null;
  name?: string | null;
  currency?: string | null;
  units?: string | null;
};

export type SpotHistoryTableRowType = {
  date: string;
  values: string[];
};

export type MarketsTableType = BaseMarketsComponentType & {
  type: 'table';
  tabs?: CommoditiesTableTabType[];
} & CommoditiesTableTabType;

export type MarketsMultiGroupTableType = BaseMarketsComponentType & {
  type: 'multi_group_table';
  tabs?: MultiGroupTableTabType[];
} & MultiGroupTableTabType;

export type MarketsTilesType = BaseMarketsComponentType & {
  type: 'tiles';
  tiles: TileType[];
};

export type MarketsGraphType = BaseMarketsComponentType & {
  type: 'graph';
  tabs?: CommoditiesTableTabType[];
} & ChartsBlockTabType<unknown>;

export type MarketsFeedType = BaseMarketsComponentType & {
  type: 'feed';
  tag?: JSX.Element;
};

export type MarketsMonthlyRecapType = BaseMarketsComponentType & {
  type: 'monthly_recap';
};

export type MarketsMinitableType = BaseMarketsComponentType & {
  type: 'minitable';
  tabs?: CommoditiesTableTabType[];
} & MiniTableTableTab;

export type CSVTableTabType = BaseMarketsComponentType & {
  type: 'csvtable';
  fileName: string;
  tabs?: CommoditiesTableTabType[];
  groupName: string;
};

export type MarketsCustomChartType = BaseMarketsComponentType & {
  type: 'graph-custom';
  tabs?: ChartsBlockTabType<unknown>[];
};

export type MarketsStaticDataChartType = BaseMarketsComponentType & {
  type: 'graph';
  metadata: {
    graphType: 'static';
  };
  tool_call_id?: string;
  chartConfig: Record<string, unknown>;
};

export type FilesType = BaseMarketsComponentType & {
  type: 'file_list';
  groupName: string;
  size?: number;
};

export type MarketsSpotHistoryTableType = BaseMarketsComponentType & {
  type: 'spot_history_table';
  columns: SpotHistoryTableColumnType[];
  rows: SpotHistoryTableRowType[];
  items?: Array<{
    name: string;
    symbol: {
      key: {
        Code: string;
        PeriodType: string;
        Period: string;
        TimeRef: string;
      };
      group: 'Prod_Indexes';
    };
  }>;
};

export type BaseTabType =
  | MarketsTableType
  | MarketsMultiGroupTableType
  | MarketsTilesType
  | MarketsGraphType
  | MarketsFeedType
  | MarketsMonthlyRecapType
  | MarketsMinitableType
  | CSVTableTabType
  | MarketsCustomChartType
  | MarketsStaticDataChartType
  | MarketsSpotHistoryTableType
  | FilesType;

export type MarketsComponentType = {
  layout?: {
    fullWidth?: boolean;
    height?: number;
  };
  component?: BaseTabType;
  tabs?: BaseTabType[];
  id: string;
};

export type DataMarketsComponentType = {
  layout?: {
    fullWidth?: boolean;
    height?: number;
  };
  metadata?: Record<string, string>;
  id: string;
};

export type DataTableType = DataMarketsComponentType & {
  type: 'table';
  tabs: CommoditiesTableTabType[];
};

export type DataMultiGroupTableType = DataMarketsComponentType & {
  type: 'multi_group_table';
  tabs: MultiGroupTableTabType[];
};

export type DataTilesType = DataMarketsComponentType & {
  type: 'tiles';
  tiles: TileType[];
};

export type DataGraphType = DataMarketsComponentType & {
  type: 'graph';
  tabs: ChartsBlockTabType<unknown>[];
};

export type DataFeedType = DataMarketsComponentType & {
  type: 'feed';
};

export type DataMonthlyRecapType = Omit<DataMarketsComponentType, 'metadata'> & {
  type: 'monthly_recap';
  metadata?: { KeyCategory?: string | string[]; KeyValue?: string; mode?: 'single' | 'list' };
};

export type DataMinitableType = DataMarketsComponentType & {
  type: 'minitable';
  tabs: MiniTableTableTab[];
};

export type DataFilesType = DataMarketsComponentType & {
  type: 'file_list';
  groupName: string;
  size?: number;
  name?: string;
};

export type DataSpotHistoryTableType = DataMarketsComponentType & {
  type: 'spot_history_table';
  name?: string | null;
  columns: SpotHistoryTableColumnType[];
  rows: SpotHistoryTableRowType[];
};

export type DataStaticDataChartType = DataMarketsComponentType & {
  type: 'graph';
  metadata: {
    graphType: 'static';
  };
  tool_call_id?: string;
  name?: string | null;
  chartConfig: Record<string, unknown>;
};

export type DataTabContainerType = DataMarketsComponentType & {
  type: 'tabcontainer';
  name?: string | null;
  tabs: Array<Partial<BaseTabType> & { type: BaseTabType['type'] }>;
};

export type DataComponentType =
  | DataTableType
  | DataMultiGroupTableType
  | DataTilesType
  | DataGraphType
  | DataFeedType
  | DataMonthlyRecapType
  | DataMinitableType
  | DataSpotHistoryTableType
  | DataStaticDataChartType
  | DataTabContainerType
  | DataFilesType;

export type DataPageType = {
  id: string;
  name: string;
  components: DataComponentType[];
};

export type MarketsPageType = {
  id: string;
  name: string;
  components: MarketsComponentType[];
};

export type Market = {
  metadata: {
    displayName: string;
    commodity: string[];
    region: string[];
    comingSoon: boolean;
    feedKeyCategories?: string[];
  };
  pages: MarketsPageType[];
  id: string;
};

export type MarketDto = {
  version?: 'v2';
  metadata: {
    displayName: string;
    commodity: string[];
    region: string[];
    comingSoon: boolean;
  };
  pages: DataPageType[];
  id: string;
};
